import { Node } from "@xyflow/react";
import { Suspense } from "react";
import { useAtomValue } from "jotai";
import {
  interfacePageAtomFamily_SUSPENDS,
  nodeAtomFamily,
  DefaultNodeData,
  suggestedNodeAtomFamily,
  extractNodeIdFromSuggestedNodeId,
} from "@zapier/canvas-state";
import ReactMarkdown from "react-markdown";
import { components } from "@zapier/api-clients/interfaces/types";

import { Kanban } from "./Kanban";
import { Table } from "./Table";
import styles from "./InterfacePreview.module.css";

interface InterfacePreviewProps {
  nodeId: string;
}

type MarkdownTextBlock = components["schemas"]["MarkdownTextBlock"];

interface FieldBlockConfig {
  id: string;
  label: string;
  placeholder?: string;
  helpText?: string;
  inputType: string;
  required?: boolean;
  readOnly?: boolean;
}

interface FormBlockConfig {
  id: string;
  submitText?: string;
}

const renderFieldBlock = (field: { config: FieldBlockConfig }) => {
  const { config } = field;
  let inputType = config.inputType;

  // Map custom input types to HTML input types
  switch (inputType) {
    case "phone-number":
      inputType = "tel";
      break;
    case "email":
      inputType = "email";
      break;
    default:
      inputType = "text";
  }

  return (
    <div key={config.id} className={styles.formField}>
      <label>
        {config.label}
        {config.required && <span className={styles.required}>*</span>}
      </label>
      <input
        type={inputType}
        placeholder={config.placeholder}
        readOnly={config.readOnly}
      />
      {config.helpText && (
        <div className={styles.helpText}>{config.helpText}</div>
      )}
    </div>
  );
};

const renderFormBlock = (block: {
  config: FormBlockConfig;
  children: any[];
}) => {
  const { config, children } = block;

  return (
    <div key={config.id} className={styles.formBlock}>
      <form onSubmit={(e) => e.preventDefault()}>
        {children?.map((field) => {
          if (field.type === "field-block") {
            return renderFieldBlock(field);
          }
          return null;
        })}
        {config.submitText && (
          <button type="submit" className={styles.submitButton}>
            {config.submitText}
          </button>
        )}
      </form>
    </div>
  );
};

const renderBlock = (block: any) => {
  switch (block.type) {
    case "form-block":
      return renderFormBlock(block);
    case "kanban-block":
      return renderKanbanBlock(block.config?.id);
    case "table-listing-block":
      return renderTableBlock(block.config?.id);
    case "markdown-text-block":
      return renderMarkdownTextBlock(block);
    default:
      return (
        <div key={block.config?.id} className={styles.unknownBlock}>
          {block.type} block
        </div>
      );
  }
};

const SuspendedComponent = ({ nodeId }: InterfacePreviewProps) => {
  const node = useAtomValue(nodeAtomFamily(nodeId)) as Node<DefaultNodeData>;
  const suggestedNode = useAtomValue(suggestedNodeAtomFamily(nodeId));
  const nodeOrSuggestedNode = node ?? suggestedNode;

  const page = useAtomValue(
    interfacePageAtomFamily_SUSPENDS({
      interfaceProjectId: nodeOrSuggestedNode.data.projectId,
      interfacePageId: nodeOrSuggestedNode.data.pageId,
      nodeId: extractNodeIdFromSuggestedNodeId(nodeId),
      proposalId: nodeOrSuggestedNode.data.proposalId,
    }),
  );

  if (!page) {
    return (
      <div className={styles.previewContainer}>
        <div className={styles.previewPlaceholder}>No page data available</div>
      </div>
    );
  }

  return (
    <div className={styles.previewContainer}>
      {/* Header */}
      {page.navigationEnabled && (
        <header className={styles.header}>
          <div className={styles.headerContent}>
            <h1>{page.name || page.title}</h1>
          </div>
        </header>
      )}

      {/* Main Content Area */}
      <main className={styles.mainContent}>
        <div className={styles.pageContent}>
          {!page.navigationEnabled && <h1>{page.name || page.title}</h1>}
          {page.blocks?.map((block) => renderBlock(block))}
        </div>
      </main>
    </div>
  );
};

const renderKanbanBlock = (key: string | undefined) => {
  return <Kanban key={key} />;
};

const renderTableBlock = (key: string | undefined) => {
  return <Table key={key} />;
};

export const InterfacePreview = ({ nodeId }: InterfacePreviewProps) => {
  return (
    <Suspense fallback={null}>
      <SuspendedComponent nodeId={nodeId} />
    </Suspense>
  );
};

const renderMarkdownTextBlock = (block: MarkdownTextBlock) => {
  return (
    <div key={block.config?.id} className={styles.markdownTextBlock}>
      <div className={styles.markdownTextBlockContent}>
        <ReactMarkdown>{block.config?.content}</ReactMarkdown>
      </div>
    </div>
  );
};
